// Weer: niet-blokkerend. Start direct met cache (of "Weer onbekend") + bewaarde simulatie,
// haalt live weer op de achtergrond op, ververst bij terugkeer (> 30 min oud) en bij 'online'.
// Locatie is opt-in: alleen gebruikt als toestemming al 'granted' is; useMyLocation vraagt erom
// op gebruikersactie. Alle sleutels zijn die van de oude app. Port van legacy sectie 4.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { WeatherKind, WeatherState, WindLevel } from '../domain/types';
import {
  applySimulation,
  DEFAULT_COORDS,
  fetchWeather,
  formatTemp,
  parseLegacySimulation,
  parseStoredCoords,
  readWeatherCache,
  roundCoord,
  SIMULATION_DEFAULT_TEMP_C,
  toWeatherCache,
  unknownWeather,
  WEATHER_LABELS,
  WEATHER_REFRESH_AFTER_MS,
  WIND_DEFAULT_KMH,
  windDisplay,
  type WeatherSimulation,
} from '../domain/weather';
import { KEYS } from '../storage/keys';
import { getRaw, getString, removeKey, setJSON, setRaw } from '../storage/localStore';
import type { WeatherApi } from './contracts';
import { useToast } from './ToastProvider';

const WeatherContext = createContext<WeatherApi | null>(null);

type Coords = { lat: number; lon: number };

function readCoords(): Coords | null {
  return parseStoredCoords(getRaw(KEYS.weatherCoords));
}

function readSimulation(): WeatherSimulation {
  return parseLegacySimulation({
    simulated: getString(KEYS.weatherSimulated),
    state: getString(KEYS.weatherState),
    temp: getString(KEYS.weatherTemp),
    windSimulated: getString(KEYS.windSimulated),
    windLevel: getString(KEYS.windLevel),
    windKmh: getString(KEYS.windKmh),
  });
}

function getDevicePosition(): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocatie niet ondersteund'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: roundCoord(pos.coords.latitude), lon: roundCoord(pos.coords.longitude) }),
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 30 * 60 * 1000 },
    );
  });
}

export function WeatherProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const coordsRef = useRef<Coords | null>(readCoords());
  const [live, setLive] = useState<WeatherState>(() => {
    const own = coordsRef.current;
    return readWeatherCache(getRaw(KEYS.weatherCache), Date.now(), own ?? DEFAULT_COORDS, !!own) ?? unknownWeather(!!own);
  });
  const [sim, setSim] = useState<WeatherSimulation>(readSimulation);
  const [overruled, setOverruled] = useState(false);
  const liveRef = useRef(live);
  const inFlight = useRef<Promise<WeatherState> | null>(null);
  const started = useRef(false);

  const commitLive = useCallback((w: WeatherState) => {
    liveRef.current = w;
    setLive(w);
  }, []);

  /** Haalt live weer op; bij fout: cache voor deze locatie of "Weer onbekend". Nooit gooien. */
  const doFetch = useCallback(async (): Promise<WeatherState> => {
    const own = coordsRef.current;
    const coords = own ?? DEFAULT_COORDS;
    let result: WeatherState;
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new Error('Offline');
      result = await fetchWeather(coords, { usesOwnLocation: !!own });
      setJSON(KEYS.weatherCache, toWeatherCache(result, coords));
    } catch (e) {
      console.warn('Open-Meteo niet bereikbaar, laatst bekend weer wordt gebruikt:', e instanceof Error ? e.message : e);
      result = readWeatherCache(getRaw(KEYS.weatherCache), Date.now(), coords, !!own) ?? unknownWeather(!!own);
    }
    // Locatie intussen gewijzigd? Dan hoort dit resultaat er niet meer bij.
    if (coordsRef.current === own) commitLive(result);
    return result;
  }, [commitLive]);

  const refreshInternal = useCallback(async (): Promise<WeatherState> => {
    if (inFlight.current) await inFlight.current.catch(() => undefined);
    const p = doFetch();
    inFlight.current = p;
    try {
      return await p;
    } finally {
      if (inFlight.current === p) inFlight.current = null;
    }
  }, [doFetch]);

  const refresh = useCallback(async () => {
    await refreshInternal();
  }, [refreshInternal]);

  const setOwnCoords = useCallback((c: Coords | null) => {
    coordsRef.current = c;
    if (c) setJSON(KEYS.weatherCoords, { lat: c.lat, lon: c.lon, savedAt: Date.now() });
    else removeKey(KEYS.weatherCoords);
  }, []);

  /* ---------- Opstarten op de achtergrond ---------- */
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      // Vraagt NOOIT zelf om toestemming
      try {
        if (navigator.permissions?.query && navigator.geolocation) {
          const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          if (status.state === 'granted') setOwnCoords(await getDevicePosition());
          else if (status.state === 'denied' && coordsRef.current) setOwnCoords(null);
        }
      } catch {
        // Permissions API ontbreekt of positie mislukt: huidige locatie houden
      }
      await refreshInternal();
    })();
  }, [refreshInternal, setOwnCoords]);

  /* ---------- Levenscyclus ---------- */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const w = liveRef.current;
      if (w.source !== 'live' || w.fetchedAt === null || Date.now() - w.fetchedAt > WEATHER_REFRESH_AFTER_MS) {
        void refreshInternal();
      }
    };
    const onOnline = () => void refreshInternal();
    const onOffline = () => {
      // Live data meteen als 'laatst bekend' markeren
      if (liveRef.current.source === 'live') commitLive({ ...liveRef.current, source: 'cache' });
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [refreshInternal, commitLive]);

  /* ---------- Simulatie (legacy losse sleutels, platte strings) ---------- */
  const simulate = useCallback(
    (kind: WeatherKind, opts?: { tempC?: number; windLevel?: WindLevel }) => {
      if (kind === 'onbekend') return;
      const tempC = opts?.tempC !== undefined ? opts.tempC : SIMULATION_DEFAULT_TEMP_C[kind];
      setRaw(KEYS.weatherSimulated, '1');
      setRaw(KEYS.weatherState, WEATHER_LABELS[kind]);
      if (tempC !== null) setRaw(KEYS.weatherTemp, formatTemp(tempC));
      else removeKey(KEYS.weatherTemp);
      let wind = sim.wind;
      if (opts?.windLevel) {
        wind = { level: opts.windLevel, kmh: WIND_DEFAULT_KMH[opts.windLevel] };
        setRaw(KEYS.windSimulated, '1');
        setRaw(KEYS.windLevel, wind.level);
        setRaw(KEYS.windKmh, String(wind.kmh));
      }
      setSim({ weather: { kind, tempC }, wind });
      setOverruled(false);
      showToast(`Weersimulatie ingesteld: ${WEATHER_LABELS[kind]}${tempC !== null ? ` (${formatTemp(tempC)})` : ''}`);
    },
    [sim.wind, showToast],
  );

  const simulateWind = useCallback(
    (level: WindLevel) => {
      const kmh = WIND_DEFAULT_KMH[level];
      setRaw(KEYS.windSimulated, '1');
      setRaw(KEYS.windLevel, level);
      setRaw(KEYS.windKmh, String(kmh));
      setSim((s) => ({ ...s, wind: { level, kmh } }));
      showToast(`Windsnelheid simulatie: ${level} (${kmh} km/u)`);
    },
    [showToast],
  );

  const resetSimulation = useCallback(() => {
    for (const k of [KEYS.weatherSimulated, KEYS.weatherState, KEYS.weatherTemp, KEYS.windSimulated, KEYS.windLevel, KEYS.windKmh]) {
      removeKey(k);
    }
    setSim({ weather: null, wind: null });
    setOverruled(false);
    const w = liveRef.current;
    if (w.source === 'onbekend') {
      showToast('Simulatie gestopt: actueel weer is nog onbekend.');
    } else {
      const t = formatTemp(w.tempC);
      showToast(
        `${w.source === 'live' ? 'Live weer' : 'Laatst bekend weer'} hersteld: ${w.label}${t ? ` (${t})` : ''}, ${windDisplay(w.windLevel, w.windKmh).label.toLowerCase()}`,
      );
    }
    if (w.source !== 'live') void refreshInternal();
  }, [refreshInternal, showToast]);

  /* ---------- Locatie ---------- */
  const useMyLocation = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      showToast('Locatie wordt niet ondersteund in deze browser.');
      return;
    }
    try {
      const coords = await getDevicePosition();
      setOwnCoords(coords);
      const w = await refreshInternal();
      const t = formatTemp(w.tempC);
      showToast(
        w.source === 'live'
          ? `Weer voor jouw buurt: ${w.label}${t ? ` (${t})` : ''}`
          : 'Locatie opgeslagen. Het weer wordt bijgewerkt zodra je online bent.',
      );
    } catch (err) {
      const denied = !!err && typeof err === 'object' && (err as { code?: number }).code === 1;
      showToast(
        denied
          ? 'Geen toegang tot je locatie. We tonen het weer in Zwolle.'
          : 'Locatie kon niet worden bepaald. Probeer het later opnieuw.',
      );
    }
  }, [refreshInternal, setOwnCoords, showToast]);

  const useDefaultLocation = useCallback(() => {
    setOwnCoords(null);
    void refreshInternal().then(() => showToast('Het weer wordt weer voor Zwolle getoond.'));
  }, [refreshInternal, setOwnCoords, showToast]);

  const setOverruledCb = useCallback((v: boolean) => setOverruled(v), []);

  const weather = useMemo(() => applySimulation(live, sim), [live, sim]);
  const isSimulated = !!sim.weather || !!sim.wind;

  const api = useMemo<WeatherApi>(
    () => ({
      weather,
      overruled,
      setOverruled: setOverruledCb,
      simulate,
      simulateWind,
      resetSimulation,
      isSimulated,
      useMyLocation,
      useDefaultLocation,
      refresh,
    }),
    [weather, overruled, setOverruledCb, simulate, simulateWind, resetSimulation, isSimulated, useMyLocation, useDefaultLocation, refresh],
  );

  return <WeatherContext.Provider value={api}>{children}</WeatherContext.Provider>;
}

export function useWeather(): WeatherApi {
  const ctx = useContext(WeatherContext);
  if (!ctx) throw new Error('useWeather moet binnen <WeatherProvider> gebruikt worden');
  return ctx;
}
